import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import { accountRoot, accountObjectPath, engineeringObjectKey, teachingProjectId, storageUuid } from "../shared/storageLayout.mjs";

export type CacheRole = "teacher" | "student";
type CacheCategory = "engineering" | "curriculum";
type CacheRecord = { invalidated?: boolean; key: string; accountId: string; category: CacheCategory; role?: CacheRole; etag: string; type: string; bytes: Blob; size: number; checksum: string };
type DirectoryRecord = { key: string; handle: FileSystemDirectoryHandle };
type CacheInfo = { directoryName: string | null; directoryReady: boolean; supported: boolean; entries: number; bytes: number; directoryError: string | null };
const databaseName = "duanju-school-local-cache-v1";
let opened: Promise<IDBDatabase | null> | undefined;
const directoryErrors = new Map<string, string>();
const pendingPersistence = new Set<Promise<unknown>>();
// QA and explicit cache operations can wait for best-effort media persistence.
export async function flushTrainingCacheWrites() {
  while (pendingPersistence.size) await Promise.allSettled([...pendingPersistence]);
}
const digest = (value: string) => bytesToHex(sha256(new TextEncoder().encode(value)));
const checksumOf = async (bytes: Blob) => {
  const buffer = await bytes.arrayBuffer();
  // Secure origins use the browser's asynchronous digest instead of hashing
  // large media/engineering files on the UI thread. HTTP retains compatibility.
  if (globalThis.crypto?.subtle) return bytesToHex(new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", buffer)));
  return bytesToHex(sha256(new Uint8Array(buffer)));
};
const directoryKey = (id: string) => `directory:account:${storageUuid(id)}`;
const generationKey = (id: string) => `generation:${storageUuid(id)}`;
const fileGenerationKey = (key: string) => `file-generation:${key}`;
const aborted = () => new DOMException("账号或会话已变更，请重新打开工作台。", "AbortError");
const assertCurrent = (current: () => boolean) => { if (!current()) throw aborted(); };

function database() {
  if (!opened) opened = new Promise(resolve => {
    try {
      const request = indexedDB.open(databaseName, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore("files", { keyPath: "key" });
        request.result.createObjectStore("settings", { keyPath: "key" });
      };
      request.onsuccess = () => {
        request.result.onversionchange = () => { request.result.close(); opened = undefined; };
        resolve(request.result);
      };
      request.onerror = request.onblocked = () => resolve(null);
    } catch { resolve(null); }
  });
  return opened;
}
async function getRecord<T>(store: "files" | "settings", key: string): Promise<T | null> {
  const db = await database();
  if (!db) return null;
  return new Promise(resolve => {
    try {
      const request = db.transaction(store).objectStore(store).get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}
async function changeRecord(store: "files" | "settings", key: string, record?: object) {
  const db = await database();
  if (!db) throw new Error("浏览器缓存不可访问，请检查站点存储权限。");
  return new Promise<void>((resolve, reject) => {
    try {
      const tx = db.transaction(store, "readwrite");
      if (record) tx.objectStore(store).put(record); else tx.objectStore(store).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = tx.onabort = () => reject(tx.error || new Error("缓存写入失败。"));
    } catch (error) { reject(error); }
  });
}
async function records(accountId: string): Promise<CacheRecord[]> {
  const db = await database();
  if (!db) return [];
  const prefix = `${accountRoot(accountId)}/`;
  return new Promise((resolve, reject) => {
    const result: CacheRecord[] = [];
    try {
      const request = db.transaction("files").objectStore("files").openCursor(IDBKeyRange.bound(prefix, `${prefix}\uffff`));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) { resolve(result); return; }
        const entry = cursor.value as CacheRecord;
        if (entry.key.startsWith(prefix) && entry.accountId === storageUuid(accountId)) result.push(entry);
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    } catch (error) { reject(error); }
  });
}
async function generation(accountId: string) {
  return (await getRecord<{ value: number }>("settings", generationKey(accountId)))?.value || 0;
}
async function fileGeneration(key: string) {
  return (await getRecord<{ value: number }>("settings", fileGenerationKey(key)))?.value || 0;
}
// Web Locks span the portal, iframe and every tab for this origin. The generation
// lives in IndexedDB, so a request started before a clear cannot repopulate it.
// A browser without cross-context locks keeps using authenticated network reads.
async function locked<T>(accountId: string, work: () => Promise<T>): Promise<T | undefined> {
  if (!globalThis.navigator?.locks) return undefined;
  return navigator.locks.request(`duanju-school-cache:${storageUuid(accountId)}`, work);
}
async function advanceGeneration(accountId: string) {
  const key = generationKey(accountId);
  await changeRecord("settings", key, { key, value: (await generation(accountId)) + 1 });
}
export async function invalidateTrainingCacheSession(accountId: string) {
  await locked(accountId, () => advanceGeneration(accountId));
}

type PermittedHandle = FileSystemDirectoryHandle & {
  queryPermission?: (options: { mode: "readwrite" }) => Promise<PermissionState>;
  requestPermission?: (options: { mode: "readwrite" }) => Promise<PermissionState>;
};
const picker = () => (window as Window & { showDirectoryPicker?: (options: { mode: "readwrite" }) => Promise<FileSystemDirectoryHandle> }).showDirectoryPicker?.bind(window);
async function permission(handle: PermittedHandle, mode: "query" | "request") {
  if (handle.queryPermission) {
    if (await handle.queryPermission({ mode: "readwrite" }) === "granted") return true;
    if (mode === "query") return false;
  }
  if (mode === "request" && handle.requestPermission) return await handle.requestPermission({ mode: "readwrite" }) === "granted";
  return !handle.queryPermission && !handle.requestPermission;
}
async function savedDirectory(accountId: string, role: CacheRole) {
  // Adopt an existing handle only for its recorded account. Existing flat files
  // remain untouched until the user explicitly clears that account's cache.
  return await getRecord<DirectoryRecord>("settings", directoryKey(accountId))
    || await getRecord<DirectoryRecord>("settings", `directory:${role}:${accountId}`);
}
async function directory(accountId: string, role: CacheRole, mode: "query" | "request" = "query") {
  const saved = await savedDirectory(accountId, role);
  if (!saved) return null;
  return await permission(saved.handle as PermittedHandle, mode).catch(() => false) ? saved.handle : null;
}
async function fileAt(root: FileSystemDirectoryHandle, accountId: string, key: string, create: boolean) {
  accountObjectPath(accountId, key);
  const segments = key.split("/");
  const name = segments.pop()!;
  let parent = root;
  for (const segment of segments) parent = await parent.getDirectoryHandle(segment, { create });
  return { parent, name, file: await parent.getFileHandle(name, { create }) };
}
async function writeMirror(root: FileSystemDirectoryHandle, record: CacheRecord) {
  const { file } = await fileAt(root, record.accountId, record.key, true);
  const writer = await file.createWritable();
  try { await writer.write(record.bytes); await writer.close(); }
  catch (error) { await writer.abort().catch(() => undefined); throw error; }
  if (await checksumOf(await file.getFile()) !== record.checksum) throw new Error("磁盘缓存回读校验失败。");
}
async function readMirror(root: FileSystemDirectoryHandle, record: CacheRecord) {
  try {
    const { file } = await fileAt(root, record.accountId, record.key, false);
    const bytes = await file.getFile();
    return await checksumOf(bytes) === record.checksum ? bytes : null;
  } catch { return null; }
}
async function removeMirror(root: FileSystemDirectoryHandle, accountId: string, key: string) {
  try { const { parent, name } = await fileAt(root, accountId, key, false); await parent.removeEntry(name); }
  catch (error) { if ((error as DOMException).name !== "NotFoundError") throw error; }
}
async function writeIndex(root: FileSystemDirectoryHandle, accountId: string) {
  const entries = (await records(accountId)).map(({ key, category, role, etag, size, checksum, type, invalidated }) => ({ key, category, role, etag, size, checksum, type, invalidated }));
  const key = `${accountRoot(accountId)}/.fanxingCache/indexes/duanju-school/index.json`;
  const { file } = await fileAt(root, accountId, key, true);
  const writer = await file.createWritable();
  try {
    await writer.write(JSON.stringify({ schema: "duanju.school.cache-index.v2", accountId: storageUuid(accountId), entries }, null, 2));
    await writer.close();
  } catch (error) { await writer.abort().catch(() => undefined); throw error; }
}
async function restoreDiskIndex(root: FileSystemDirectoryHandle, accountId: string) {
  let indexFile: FileSystemFileHandle;
  try { indexFile = (await fileAt(root, accountId, `${accountRoot(accountId)}/.fanxingCache/indexes/duanju-school/index.json`, false)).file; }
  catch (error) { if ((error as DOMException).name === "NotFoundError") return; throw error; }
  const file = await indexFile.getFile();
  if (file.size > 16 * 1024 * 1024) throw new Error("目录缓存索引过大，原文件已保留。");
  let index: { schema?: string; accountId?: string; entries?: unknown[] };
  try { index = JSON.parse(await file.text()); } catch { throw new Error("目录缓存索引损坏，原文件已保留。"); }
  if (index.schema !== "duanju.school.cache-index.v2" || index.accountId !== storageUuid(accountId) || !Array.isArray(index.entries) || index.entries.length > 100_000) throw new Error("目录缓存索引不属于当前账号，原文件已保留。");
  const known = new Set((await records(accountId)).map(row => row.key));
  for (const value of index.entries) {
    const row = value as CacheRecord;
    const rel = accountObjectPath(accountId, row.key);
    const validPath = row.category === "engineering"
      ? /^engineeringProjects\/[0-9a-f-]{36}\.fanxing\/.+/.test(rel)
      : row.category === "curriculum" && /^curriculum\/(teacher|student)\/[a-z0-9_-]+\.json$/i.test(rel);
    if (!validPath || !["teacher", "student"].includes(row.role || "") || typeof row.etag !== "string" || !row.etag || row.etag.length > 4096
      || !/^[a-f0-9]{64}$/.test(row.checksum) || !Number.isSafeInteger(row.size) || row.size < 0 || row.size > 64 * 1024 * 1024
      || typeof row.type !== "string" || row.type.length > 256) throw new Error("目录缓存条目校验失败，原文件已保留。");
    if (known.has(row.key)) continue;
    const record = { ...row, accountId: storageUuid(accountId) };
    const bytes = await readMirror(root, record);
    if (!bytes) throw new Error("目录缓存文件缺失或损坏，原索引已保留。");
    await changeRecord("files", row.key, { ...record, bytes });
  }
}
async function backfill(root: FileSystemDirectoryHandle, accountId: string) {
  for (const record of await records(accountId)) {
    if (!record.invalidated && record.bytes instanceof Blob && await checksumOf(record.bytes) === record.checksum) await writeMirror(root, record);
  }
  await writeIndex(root, accountId);
}
export async function pickTrainingCacheDirectory(accountId: string, role: CacheRole) {
  const choose = picker();
  if (!window.isSecureContext || !choose || !navigator.locks) throw new Error("选择可写目录需要安全地址及支持此功能的浏览器；网络读取仍可使用。");
  const handle = await choose({ mode: "readwrite" });
  if (!await permission(handle as PermittedHandle, "request")) throw new Error("没有获得所选目录的读写权限。");
  await locked(accountId, async () => {
    const key = directoryKey(accountId);
    await restoreDiskIndex(handle, accountId);
    await backfill(handle, accountId);
    await changeRecord("settings", key, { key, handle });
    // The selected account root replaces role-specific bindings. Old physical
    // copies remain preserved; retired handles must never silently reappear.
    for (const oldRole of ["teacher", "student"]) await changeRecord("settings", `directory:${oldRole}:${accountId}`);
    directoryErrors.delete(key);
  });
  return handle.name;
}
export async function removeTrainingCacheDirectory(accountId: string, _role: CacheRole) {
  await locked(accountId, async () => {
    for (const key of [directoryKey(accountId), `directory:teacher:${accountId}`, `directory:student:${accountId}`]) await changeRecord("settings", key);
  });
}
export async function restoreTrainingCacheDirectory(accountId: string, role: CacheRole) {
  const root = await directory(accountId, role, "request");
  if (!root) throw new Error("目录权限未恢复，请重新选择目录。");
  await locked(accountId, async () => { await restoreDiskIndex(root, accountId); await backfill(root, accountId); directoryErrors.delete(directoryKey(accountId)); });
}
export async function getScopedCacheInfo(accountId: string, category: CacheCategory, role?: CacheRole) {
  const entries = (await records(accountId)).filter(row => row.category === category && (!role || row.role === role));
  return { entries: entries.length, bytes: entries.reduce((sum, row) => sum + row.size, 0) };
}
export async function getTrainingCacheInfo(accountId: string, role: CacheRole): Promise<CacheInfo> {
  const saved = await savedDirectory(accountId, role);
  return { ...await getScopedCacheInfo(accountId, "engineering"), directoryName: saved?.handle.name || null,
    directoryReady: Boolean(await directory(accountId, role)), supported: Boolean(picker()) && window.isSecureContext && Boolean(navigator.locks),
    directoryError: (await getRecord<{ message: string }>("settings", `error:${accountId}`))?.message || directoryErrors.get(directoryKey(accountId)) || null };
}
export async function backfillTrainingCache(accountId: string, role: CacheRole) {
  await locked(accountId, async () => { const root = await directory(accountId, role); if (root) await backfill(root, accountId); });
}
async function removeLegacyBrowserRecords(accountId: string) {
  const db = await database();
  if (!db) throw new Error("浏览器缓存不可访问。");
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction("files", "readwrite"), request = tx.objectStore("files").openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      if (String(cursor.key).startsWith(`${accountId}:`)) cursor.delete();
      cursor.continue();
    };
    tx.oncomplete = () => resolve(); tx.onerror = tx.onabort = () => reject(tx.error);
  });
}
async function removeLegacyCourseRecords(accountId: string, role?: CacheRole) {
  const db = await new Promise<IDBDatabase | null>(resolve => {
    try {
      const request = indexedDB.open("duanju-school-curriculum-cache-v1");
      request.onupgradeneeded = () => request.transaction?.abort();
      request.onsuccess = () => resolve(request.result);
      request.onerror = request.onblocked = () => resolve(null);
    } catch { resolve(null); }
  });
  if (!db) return;
  try {
    if (!db.objectStoreNames.contains("courses")) return;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction("courses", "readwrite"), request = tx.objectStore("courses").openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        const key = String(cursor.key);
        if ((role ? [role] : ["teacher", "student"]).some(value => key.startsWith(`${value}:${accountId}:`)) || key.startsWith(`${accountId}:`)) cursor.delete();
        cursor.continue();
      };
      tx.oncomplete = () => resolve(); tx.onerror = tx.onabort = () => reject(tx.error);
    });
  } finally { db.close(); }
}
async function removeScriptWorkspaceCache(accountId: string) {
  const db = await new Promise<IDBDatabase | null>(resolve => {
    try {
      const request = indexedDB.open("fanxing-script-workspace-cache", 1);
      request.onupgradeneeded = () => request.transaction?.abort();
      request.onsuccess = () => resolve(request.result);
      request.onerror = request.onblocked = () => resolve(null);
    } catch { resolve(null); }
  });
  if (!db) return;
  try {
    if (!db.objectStoreNames.contains("workspaces")) return;
    const prefix = `node:/teaching-copy/accounts/${encodeURIComponent(accountId)}::`;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction("workspaces", "readwrite"), request = tx.objectStore("workspaces").openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        if (String(cursor.key).startsWith(prefix) && cursor.value?.schema === "fanxing.script.workspace-cache.v1") cursor.delete();
        cursor.continue();
      };
      tx.oncomplete = () => resolve(); tx.onerror = tx.onabort = () => reject(tx.error);
    });
  } finally { db.close(); }
}
async function removeOwnedDiskFiles(root: FileSystemDirectoryHandle, accountId: string, category?: CacheCategory, role?: CacheRole) {
  for (const record of await records(accountId)) {
    if ((!category || record.category === category) && (!role || record.role === role)) await removeMirror(root, accountId, record.key);
  }
  if (category) return;
  // Remove only indexed files and empty parents. A selected folder may also
  // contain another app's engineering tree or hand-placed documents.
  const indexKey = `${accountRoot(accountId)}/.fanxingCache/indexes/duanju-school/index.json`;
  await removeMirror(root, accountId, indexKey);
  const parents = new Set<string>();
  for (const key of [...(await records(accountId)).map(record => record.key), indexKey]) {
    const parts = key.split("/"); parts.pop();
    while (parts.length > 1) { parents.add(parts.join("/")); parts.pop(); }
  }
  for (const path of [...parents].sort((a, b) => b.split("/").length - a.split("/").length)) {
    const parts = path.split("/"), name = parts.pop()!;
    try {
      let parent = root;
      for (const part of parts) parent = await parent.getDirectoryHandle(part);
      await parent.removeEntry(name); // Deliberately non-recursive.
    } catch (error) {
      if (!["NotFoundError", "InvalidModificationError"].includes((error as DOMException).name)) throw error;
    }
  }
  const prefixes = (["teacher", "student"] as const).map(value => `account-${digest(`${value}:${accountId}`).slice(0, 32)}-`);
  const entries = root as FileSystemDirectoryHandle & { entries: () => AsyncIterable<[string, FileSystemHandle]> };
  const names: string[] = [];
  for await (const [name, handle] of entries.entries()) {
    if (handle.kind === "file" && prefixes.some(prefix => name.startsWith(prefix) && /^[a-f0-9]{64}\.cache$/.test(name.slice(prefix.length)))) names.push(name);
  }
  for (const name of names) await root.removeEntry(name);
}
export async function clearScopedCache(accountId: string, category?: CacheCategory, role?: CacheRole) {
  if (!navigator.locks) throw new Error("当前浏览器无法协调缓存清理，请使用支持站点存储的安全地址。");
  await locked(accountId, async () => {
    await advanceGeneration(accountId);
    const message = { type: "clear", accountId: storageUuid(accountId) };
    window.dispatchEvent(new CustomEvent("duanju-school-cache", { detail: message }));
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel("duanju-school-cache"); channel.postMessage(message); channel.close();
    }
    // Old teacher/student handles may differ. Clear only this account in each.
    const handles: FileSystemDirectoryHandle[] = [];
    for (const cacheRole of ["teacher", "student"] as const) {
      const saved = await savedDirectory(accountId, cacheRole);
      if (!saved) continue;
      const root = await directory(accountId, cacheRole);
      if (!root) throw new Error("无法清理已选磁盘目录；请重新授权目录后再清空缓存。");
      if (!handles.some(handle => handle === root)) handles.push(root);
    }
    for (const root of handles) await restoreDiskIndex(root, accountId);
    for (const root of handles) await removeOwnedDiskFiles(root, accountId, category, role);
    for (const record of await records(accountId)) {
      if ((!category || record.category === category) && (!role || record.role === role)) await changeRecord("files", record.key);
    }
    if (!category) { await removeLegacyBrowserRecords(accountId); await removeScriptWorkspaceCache(accountId); }
    if (!category || category === "curriculum") await removeLegacyCourseRecords(accountId, role);
    await changeRecord("settings", `error:${accountId}`);
    if (category) for (const root of handles) await writeIndex(root, accountId);
    directoryErrors.delete(directoryKey(accountId));
  });
}
export const clearTrainingCache = (accountId: string, _role: CacheRole) => clearScopedCache(accountId);

async function evict(accountId: string, role: CacheRole, key: string) {
  await locked(accountId, async () => {
    // A saved/deleted file invalidates only its own in-flight reads. Advancing
    // the account clear barrier here would disable the mounted workspace's
    // cache write queue for the remainder of its session.
    const epochKey = fileGenerationKey(key);
    await changeRecord("settings", epochKey, { key: epochKey, value: (await fileGeneration(key)) + 1 });
    const record = await getRecord<CacheRecord>("files", key);
    // Keep a tombstone until the bound disk file is removed. If permission is
    // lost, a later clear must still know exactly which file belongs to us.
    if (record) await changeRecord("files", key, { ...record, invalidated: true });
    const saved = await savedDirectory(accountId, role);
    const root = await directory(accountId, role);
    if (saved && !root) return;
    if (root) await removeMirror(root, accountId, key);
    await changeRecord("files", key);
    if (root) await writeIndex(root, accountId);
  });
}
export async function cachedScopedFetch(options: {
  accountId: string; role: CacheRole; category: CacheCategory; key: string;
  request: (etag?: string) => Promise<Response>; stillCurrent: () => boolean;
  requirePathHeader?: boolean;
  deferPersistence?: boolean;
}) {
  const { accountId, role, category, key, request, stillCurrent } = options;
  accountObjectPath(accountId, key);
  assertCurrent(stillCurrent);
  const [startGeneration, startFileGeneration] = await Promise.all([generation(accountId), fileGeneration(key)]);
  const generationsCurrent = async () => {
    const [accountEpoch, fileEpoch] = await Promise.all([generation(accountId), fileGeneration(key)]);
    return accountEpoch === startGeneration && fileEpoch === startFileGeneration;
  };
  const row = await getRecord<CacheRecord>("files", key);
  const cached = row?.accountId === storageUuid(accountId) && !row.invalidated && row.bytes instanceof Blob ? row : null;
  let response = await request(cached?.etag);
  assertCurrent(stillCurrent);
  if (options.requirePathHeader && (response.ok || response.status === 304) && response.headers.get("X-Duanju-Cache-Path") !== key) throw new Error("工程响应账号不匹配，请重新打开工作台。");
  if (response.status === 304) {
    const reusable = cached && cached.etag === response.headers.get("ETag") && await generationsCurrent();
    if (reusable) {
      const root = await directory(accountId, role);
      const disk = root ? await readMirror(root, cached) : null;
      const bytes = disk || cached.bytes;
      // readMirror already verified this exact disk Blob. The IndexedDB
      // fallback still needs its own integrity check.
      const intact = disk !== null || await checksumOf(bytes) === cached.checksum;
      assertCurrent(stillCurrent);
      if (intact && await generationsCurrent()) return new Response(bytes, { status: 200, headers: {
        "Content-Type": cached.type, ETag: cached.etag, "X-Duanju-Cache": "local", "X-Duanju-Cache-Source": disk ? "directory" : "browser", "X-Duanju-Cache-Path": key,
      } });
    }
    response = await request();
    assertCurrent(stillCurrent);
    if (options.requirePathHeader && response.ok && response.headers.get("X-Duanju-Cache-Path") !== key) throw new Error("工程响应账号不匹配，请重新打开工作台。");
  }
  if ([401, 403, 404].includes(response.status)) await evict(accountId, role, key).catch(() => undefined);
  const etag = response.headers.get("ETag");
  if (response.status === 200 && etag && (!options.requirePathHeader || response.headers.get("X-Duanju-Cache-Path") === key)) {
    const size = Number(response.headers.get("Content-Length"));
    if (size > 64 * 1024 * 1024) return response;
    const bytes = await response.clone().blob();
    assertCurrent(stillCurrent);
    if (bytes.size <= 64 * 1024 * 1024) {
      const checksum = await checksumOf(bytes);
      // The curriculum ETag is a content hash; reject corruption before caching.
      const taggedHash = etag.match(/^"sha256-([a-f0-9]{64})"$/)?.[1];
      if (taggedHash && taggedHash !== checksum) throw new Error("缓存内容校验失败，请重试。");
      const persist = locked(accountId, async () => {
        if (!stillCurrent() || !await generationsCurrent()) return;
        const record: CacheRecord = { key, accountId: storageUuid(accountId), category, role, bytes, etag, checksum, size: bytes.size, type: response.headers.get("Content-Type") || "application/octet-stream" };
        await changeRecord("files", key, record);
        const root = await directory(accountId, role);
        if (!root) return;
        try { await writeMirror(root, record); await writeIndex(root, accountId); directoryErrors.delete(directoryKey(accountId)); await changeRecord("settings", `error:${accountId}`); }
        catch (error) { const message = error instanceof Error ? error.message : "磁盘缓存写入失败。"; directoryErrors.set(directoryKey(accountId), message); await changeRecord("settings", `error:${accountId}`, { key: `error:${accountId}`, message }); }
      }).catch(() => undefined); // Network reads continue when browser storage is unavailable.
      if (options.deferPersistence) {
        pendingPersistence.add(persist);
        void persist.finally(() => pendingPersistence.delete(persist));
      } else await persist;
    }
  }
  assertCurrent(stillCurrent);
  return response;
}
export async function cachedDemonstrationFetch(
  accountId: string, url: URL, init: RequestInit | undefined,
  request: (input: string, options?: RequestInit) => Promise<Response>, stillCurrent: () => boolean = () => true,
) {
  const path = url.searchParams.get("path"), method = (init?.method || "GET").toUpperCase();
  if (!path || url.pathname !== "/api/demonstrations/file" || init?.signal?.aborted || new Headers(init?.headers).has("Range") || method === "HEAD") return request(url.href, init);
  const key = engineeringObjectKey(accountId, teachingProjectId, path);
  if (method !== "GET") {
    const response = await request(url.href, init);
    assertCurrent(stillCurrent);
    if (response.ok) await evict(accountId, "teacher", key).catch(() => undefined);
    return response;
  }
  return cachedScopedFetch({ accountId, role: "teacher", category: "engineering", key, stillCurrent,
    requirePathHeader: true,
    // Verified media can render while its disk copy and index are written.
    // Authored JSON/curriculum reads keep their synchronous persistence contract.
    deferPersistence: /\.(png|jpe?g|webp|gif|avif|mp4|webm|mov|mp3|wav|m4a|ogg)$/i.test(path),
    request: etag => {
      const headers = new Headers(init?.headers);
      if (etag) headers.set("If-None-Match", etag); else headers.delete("If-None-Match");
      return request(url.href, { ...init, headers });
    },
  });
}
